import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { FlaskConical, Eye, EyeOff, Presentation } from "lucide-react";
import RichContent from "@/components/RichContent";
import VideoEmbed from "@/components/VideoEmbed";
import { mediaUrl } from "@/lib/api";

function ImageBlock({ b }) {
  const align = b.align || "center";
  const width = b.width || 100;
  const style = { width: `${width}%`, maxWidth: "100%" };
  if (align === "left") Object.assign(style, { float: "left", marginRight: "1.75rem", marginBottom: "1rem", marginTop: "0.25rem" });
  else if (align === "right") Object.assign(style, { float: "right", marginLeft: "1.75rem", marginBottom: "1rem", marginTop: "0.25rem" });
  else Object.assign(style, { display: "block", margin: "1.5rem auto", clear: "both" });
  if (!b.url) return null;
  return (
    <figure style={style} className="mb-2">
      <img src={mediaUrl(b.url)} alt={b.caption || ""} className="w-full border border-paper-200" />
      {b.caption && <figcaption className="text-sm text-ink-500 mt-2 text-center">{b.caption}</figcaption>}
    </figure>
  );
}

function PptBlock({ b }) {
  if (!b.url) return null;
  return (
    <div className="my-8 clear-both" data-testid="block-presentation">
      <div className="flex items-center gap-3 mb-4"><span className="accent-rule" /><span className="eyebrow text-nasa-blue">PRESENTACIÓN</span></div>
      <div className="relative w-full h-[420px] sm:h-[560px] border border-paper-200 bg-paper-50">
        <iframe
          title="Presentación"
          src={`https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(mediaUrl(b.url))}`}
          className="absolute inset-0 w-full h-full"
          frameBorder="0"
          allowFullScreen
        />
      </div>
      <div className="mt-3 flex flex-wrap items-center gap-2 text-sm text-ink-500">
        <Presentation size={15} className="text-nasa-blue" />
        Usa las flechas del visor para pasar o retroceder las diapositivas.
        <a href={mediaUrl(b.url)} target="_blank" rel="noreferrer" className="font-semibold text-nasa-blue hover:underline" data-testid="block-presentation-download">Descargar</a>
      </div>
    </div>
  );
}

function PracticeBlock({ b, index }) {
  const [show, setShow] = useState(false);
  return (
    <div className="my-6 clear-both bg-white border border-paper-200 border-l-4 border-l-nasa-blue p-6" data-testid={`practice-item-${index}`}>
      <div className="flex items-center gap-2 eyebrow text-nasa-blue mb-3"><FlaskConical size={14} /> PROBLEMA {String(index + 1).padStart(2, "0")}</div>
      <RichContent html={b.problem} />
      <button
        onClick={() => setShow((s) => !s)}
        data-testid={`toggle-solution-${index}`}
        className="mt-5 inline-flex items-center gap-2 text-sm font-semibold px-4 py-2 rounded-full border border-nasa-blue text-nasa-blue hover:bg-nasa-blue hover:text-white transition-colors"
      >
        {show ? <><EyeOff size={15} /> Ocultar solución</> : <><Eye size={15} /> Ver la solución</>}
      </button>
      <AnimatePresence>
        {show && (
          <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="overflow-hidden">
            <div className="mt-5 pt-5 border-t border-paper-200 bg-paper-50 -mx-6 -mb-6 px-6 pb-6 pt-5">
              <div className="eyebrow text-nasa-blue mb-2">SOLUCIÓN</div>
              <RichContent html={b.solution} />
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default function ContentBlocks({ blocks = [], textClassName = "" }) {
  let practiceCount = 0;
  return (
    <div className="content-blocks">
      {blocks.map((b, i) => {
        if (b.type === "text") return <RichContent key={b.id || i} html={b.html} className={textClassName} />;
        if (b.type === "image") return <ImageBlock key={b.id || i} b={b} />;
        if (b.type === "video") return <div key={b.id || i} className="my-8 clear-both"><VideoEmbed url={b.url} /></div>;
        if (b.type === "powerpoint") return <PptBlock key={b.id || i} b={b} />;
        if (b.type === "practice") { const idx = practiceCount++; return <PracticeBlock key={b.id || i} b={b} index={idx} />; }
        return null;
      })}
    </div>
  );
}
